import { Nullable } from 'global';
import { logError } from 'utils/logger';
import { useMutation } from '@apollo/client';
import { useNotifications } from 'modules/notification';
import { aftermarketProductsApi } from '../api';
import {
  CreateDTO,
  UpdateDTO,
  CreateProductResponse,
  CreateProductVariables,
  UpdateProductResponse,
  UpdateProductVariables,
  AftermarketProductUpsertDocumentResponse,
  AftermarketProductUpsertDocumentVariables,
  AftermarketProductDeleteDocumentResponse,
  AftermarketProductDeleteDocumentVariables,
} from '../api/types';
import { ProductPageDocumentsState } from '../types';
import { useState } from 'react';

export function useAftermarketProducts() {
  const { showNotification } = useNotifications();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [onCreate] = useMutation<CreateProductResponse, CreateProductVariables>(aftermarketProductsApi.createProduct);

  const [onUpdate] = useMutation<UpdateProductResponse, UpdateProductVariables>(aftermarketProductsApi.updateProduct);

  const [upsertDocument] = useMutation<
    AftermarketProductUpsertDocumentResponse,
    AftermarketProductUpsertDocumentVariables
  >(aftermarketProductsApi.upsertDocument);

  const [deleteDocument] = useMutation<
    AftermarketProductDeleteDocumentResponse,
    AftermarketProductDeleteDocumentVariables
  >(aftermarketProductsApi.deleteDocument);

  const handleAftermarketProductCreate = async (
    dto: CreateDTO,
    documents?: ProductPageDocumentsState
  ): Promise<Nullable<string>> => {
    try {
      setIsLoading(true);
      const { data: createResponse } = await onCreate({ variables: { input: dto } });
      if (createResponse?.create?.aftermarketProductId) {
        await handleDocumentsUpload(createResponse.create.aftermarketProductId, documents);
      }
      showNotification('Aftermarket product created successfully!');

      return createResponse?.create?.aftermarketProductId ?? null;
    } catch (error) {
      logError(error);
      showNotification('An error occurred', { type: 'error' });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const handleAftermarketProductUpdate = async (
    dto: UpdateDTO,
    documents?: ProductPageDocumentsState
  ): Promise<Nullable<string>> => {
    try {
      setIsLoading(true);
      const { data: updateResponse } = await onUpdate({ variables: { input: dto } });
      await handleDocumentsUpload(dto.aftermarketProductId, documents);

      showNotification('Aftermarket product updated successfully!');

      return updateResponse?.update?.aftermarketProductId ?? null;
    } catch (error) {
      logError(error);
      showNotification('An error occurred', { type: 'error' });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const handleDocumentsUpload = async (aftermarketProductId: string, documents?: ProductPageDocumentsState) => {
    if (!documents) {
      return;
    }
    try {
      const { documentsToAdd, documentsToRemove } = documents;

      const upsertPromises = documentsToAdd.map(
        async file =>
          await upsertDocument({
            variables: {
              input: {
                aftermarketProductId,
              },
              file,
            },
          })
      );
      const deletePromises = documentsToRemove.map(
        async document => await deleteDocument({ variables: { id: document.documentId! } })
      );

      return Promise.all([...upsertPromises, ...deletePromises]);
    } catch (error) {
      logError(error);
      showNotification('An error occurred', { type: 'error' });
    }
  };

  return {
    isLoading,
    handleAftermarketProductCreate,
    handleAftermarketProductUpdate,
  };
}
